import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * End-user chat → editor transition (Connect #43, Topic 2).
 *
 * The carrier — a media frame — lifts off the SOURCE preview, then morphs onto
 * the editor CANVAS (creative continuity). Timing is DOM-driven, not fixed: the
 * service plays enter → hold → navigate → transit, then the overlay waits for
 * the real canvas to mount and lands the frame on it (so it never targets a
 * stale spot). The service keeps only a safety teardown timer in case the
 * overlay never completes.
 */
export type AgentTransitionPhase = 'idle' | 'enter' | 'hold' | 'transit';

export interface AgentTransitionRect {
	x: number;
	y: number;
	width: number;
	height: number;
}

export interface AgentTransitionPlayOptions {
	/** Viewport rect of the SOURCE preview — where the carrier starts. */
	source: AgentTransitionRect | null;
	/** Source centre (the carrier's spawn point). */
	origin: { x: number; y: number } | null;
	/** Performs the real router navigation. Invoked mid-flight so the swap is masked. */
	navigate: () => void;
	/**
	 * Source-element rects (viewport coords) the editor FLIPs its regions from
	 * for real continuity (Tier 2). Keyed by region — e.g. `chat` → the AI panel,
	 * `preview` → the canvas. The editor consumes each once on arrival.
	 */
	rects?: Record<string, AgentTransitionRect>;
}

export interface AgentTransitionState {
	active: boolean;
	phase: AgentTransitionPhase;
	source: AgentTransitionRect | null;
	origin: { x: number; y: number } | null;
}

const IDLE_STATE: AgentTransitionState = {
	active: false,
	phase: 'idle',
	source: null,
	origin: null
};

// Beats (ms): `holdAt`→carrier visible at the source preview; `navigateAt`→route
// swap behind it; then phase 'transit' hands off to the overlay's DOM poll.
const SCHEDULE = { holdAt: 40, navigateAt: 280 };

// Hard stop — the overlay normally tears itself down sooner via cancel().
const SAFETY_TEARDOWN_MS = 4500;

@Injectable({ providedIn: 'root' })
export class AgentTransitionService {
	private readonly state$ = new BehaviorSubject<AgentTransitionState>(IDLE_STATE);
	readonly transition$ = this.state$.asObservable();

	private timers: ReturnType<typeof setTimeout>[] = [];

	/** Source rects handed to the editor for its FLIP entrance. Set on play (for
	 *  the animated path), consumed once per key by the arriving editor regions,
	 *  cleared on reset so a later non-transition open never FLIPs. */
	private handoffRects: Record<string, AgentTransitionRect> = {};

	play(opts: AgentTransitionPlayOptions): void {
		this.clear();

		const reduceMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

		if (reduceMotion) {
			opts.navigate();
			return;
		}

		// Hand the source rects to the editor's FLIP entrance (animated path only).
		this.handoffRects = opts.rects ?? {};

		const { holdAt, navigateAt } = SCHEDULE;

		this.state$.next({
			active: true,
			phase: 'enter',
			source: opts.source,
			origin: opts.origin
		});

		this.timers.push(setTimeout(() => this.patch({ phase: 'hold' }), holdAt));
		this.timers.push(
			setTimeout(() => {
				opts.navigate();
				this.patch({ phase: 'transit' });
			}, navigateAt)
		);
		this.timers.push(setTimeout(() => this.reset(), SAFETY_TEARDOWN_MS));
	}

	/** All captured source rects (keyed by region), read by the app-root carrier
	 *  overlay to spawn its panels and fly them to the editor's regions. */
	peekHandoffRects(): Record<string, AgentTransitionRect> {
		return { ...this.handoffRects };
	}

	/** Tear the transition down. */
	cancel(): void {
		this.clear();
		this.reset();
	}

	private patch(partial: Partial<AgentTransitionState>): void {
		this.state$.next({ ...this.state$.value, ...partial });
	}

	private reset(): void {
		this.handoffRects = {};
		this.state$.next(IDLE_STATE);
	}

	private clear(): void {
		this.timers.forEach(t => clearTimeout(t));
		this.timers = [];
	}
}
